import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Agenda from './pages/Agenda';
import Patients from './pages/Patients';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Commissions from './pages/Commissions';
import Expenses from './pages/Expenses';
import Reports from './pages/Reports';
import Sales from './pages/Sales';
import Societies from './pages/Societies';
import AccountsReceivable from './pages/AccountsReceivable';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// OAuth Callback Handler - forwards to backend
const OAuthCallbackRedirect = () => {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    // Get all query parameters
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    
    if (error) {
      // If there's an error from Google, redirect to settings with error
      window.location.href = '/settings?error=auth_failed';
      return;
    }
    
    if (code) {
      // Build the backend callback URL with all query params
      const backendUrl = `${API_URL}/api/oauth/calendar/callback?code=${encodeURIComponent(code)}${state ? `&state=${encodeURIComponent(state)}` : ''}`;
      // Redirect to backend
      window.location.href = backendUrl;
    } else {
      // No code, redirect to settings with error
      window.location.href = '/settings?error=no_code';
    }
  }, [searchParams]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-light">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-brand-main/30 border-t-brand-main rounded-full animate-spin"></div>
        <p className="text-slate-600">Conectando con Google Calendar...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, hasRole } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-brand-main/30 border-t-brand-main rounded-full animate-spin"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !hasRole(allowedRoles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-light">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-brand-main/30 border-t-brand-main rounded-full animate-spin"></div>
          <p className="text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      
      {/* Protected routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/agenda" 
        element={
          <ProtectedRoute>
            <Agenda />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/pacientes" 
        element={
          <ProtectedRoute>
            <Patients />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/usuarios" 
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN']}>
            <Users />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <Settings />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/comisiones" 
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <Commissions />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/gastos" 
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <Expenses />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/informes" 
        element={
          <ProtectedRoute>
            <Reports />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/sociedades" 
        element={
          <ProtectedRoute allowedRoles={['SUPERADMIN', 'ADMIN']}>
            <Societies />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/ventas" 
        element={
          <ProtectedRoute>
            <Sales />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/cuentas-por-cobrar" 
        element={
          <ProtectedRoute>
            <AccountsReceivable />
          </ProtectedRoute>
        } 
      />
      
      {/* OAuth Callback - handles Google redirect and forwards to backend */}
      <Route 
        path="/oauth/google/callback" 
        element={<OAuthCallbackRedirect />} 
      />
      
      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
